"use client";

import { useEffect, useState } from "react";
import { useValidation } from "@/hooks";
import { registerSchema } from "@/validations/register";
import { RegisterForm, RegisterTouched } from "@/types/register";
import { useParams, useRouter } from "next/navigation";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "@/store";
import { fetchPlans } from "@/store/modules/plans/thunks";
import { registerEstablishment } from "@/store/modules/register/thunks";
import { signIn } from "next-auth/react";
import { toast } from "react-toastify";

const useRegister = () => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const params = useParams();

  const currentLang = params?.lang || "pt-BR";

  const form: RegisterForm = {
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    phone_whatsapp: "",
    origin_country: "",
    plan_id: 1,
    accept_terms: false,
  };

  const [registerForm, setRegisterForm] = useState<RegisterForm>(form);
  const [touched, setTouched] = useState<RegisterTouched>({});
  const [isLoading, setIsLoading] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);

  const { errors, validateField, validateForm } = useValidation(registerSchema);

  const serializeForValidation = (
    form: RegisterForm,
  ): { [key: string]: string } => {
    const out: { [key: string]: string } = {};
    Object.entries(form).forEach(([k, v]) => {
      out[k] =
        typeof v === "boolean" ? (v ? "true" : "false") : String(v ?? "");
    });
    return out;
  };

  const [passwordError, setPasswordError] = useState<string>("");

  const validatePasswords = (password: string, confirmation: string) => {
    if (password && confirmation && password !== confirmation) {
      return "As senhas devem ser iguais";
    }
    return "";
  };

  // const plans  = useSelector((state: any) => state.plans);

  useEffect(() => {
    dispatch(fetchPlans({}));
  }, []);

  const handleInputChange = (e: any) => {
    if (typeof e === "string") {
      setRegisterForm((prev) => ({
        ...prev,
        phone_whatsapp: e,
      }));
      validateField("phone_whatsapp", e);
      return;
    }

    const { name, value } = e.target;

    // Checkbox: aceitar termos de uso
    if (name === "accept_terms") {
      const checked = e.target.checked === true;
      setRegisterForm((prev) => ({
        ...prev,
        accept_terms: checked,
      }));
      validateField("accept_terms", checked ? "true" : "false");
      return;
    }

    if (name === "plan_id") {
      setRegisterForm((prev) => ({
        ...prev,
        plan_id: value,
      }));
      validateField("plan_id", value);
      return;
    }

    if (name === "origin_country") {
      setRegisterForm((prev) => ({
        ...prev,
        origin_country: value,
      }));
      validateField("origin_country", value);
      return;
    }

    setRegisterForm((prev) => {
      const newForm = {
        ...prev,
        [name]: value,
      };

      if (name === "password" || name === "password_confirmation") {
        const error = validatePasswords(
          name === "password" ? value : prev.password,
          name === "password_confirmation" ? value : prev.password_confirmation,
        );
        setPasswordError(error);
      }

      return newForm;
    });
    validateField(name, value);
  };

  const handleBlur = (e: any) => {
    const { name } = e.target;
    setTouched({
      ...touched,
      [name]: true,
    });
    validateField(name, String(registerForm[name as keyof RegisterForm]));
  };

  const handleRegister = async (e: any) => {
    e.preventDefault();

    const passwordsError = validatePasswords(
      registerForm.password,
      registerForm.password_confirmation,
    );

    if (passwordsError) {
      setPasswordError(passwordsError);
      return;
    }

    const isValid = await validateForm(serializeForValidation(registerForm));
    if (!isValid) {
      setTouched({
        name: true,
        email: true,
        password: true,
        password_confirmation: true,
        phone_whatsapp: true,
        origin_country: true,
        plan_id: true,
        accept_terms: true,
      });
      return;
    }

    setIsLoading(true);

    try {
      const { confirmPassword, ...registerData } = registerForm;

      const resultAction = await dispatch(registerEstablishment(registerData));

      if (registerEstablishment.fulfilled.match(resultAction)) {
        // Registro bem-sucedido
        toast.success("Conta criada com sucesso!");
        setIsRedirecting(true);

        // Fazer login automático com as credenciais usadas no registro
        const loginResult = await signIn("credentials", {
          email: registerForm.email,
          password: registerForm.password,
          redirect: false, // Não redirecionar automaticamente
        });

        if (loginResult?.ok) {
          // Login bem-sucedido, redirecionar para agenda (rota raiz agora
          // já encaminha para /schedule, mas explicito para clareza).
          toast.success("Bem-vindo! Redirecionando para a agenda...");
          // Pequeno delay para mostrar a mensagem
          setTimeout(() => {
            router.push(`/${currentLang}/schedule`);
          }, 1000);
        } else {
          // Se o login automático falhar, mostrar mensagem e redirecionar para login
          toast.warning("Conta criada! Por favor, faça login para continuar.");
          setTimeout(() => {
            router.push(`/${currentLang}/login`);
          }, 2000);
        }
      }
      // O erro já é tratado pelo slice com toast
    } catch (error) {
      console.error("Registration error:", error);
      toast.error("Erro inesperado ao criar conta");
    } finally {
      setIsLoading(false);
      setIsRedirecting(false);
    }
  };

  return {
    registerForm,
    handleInputChange,
    handleRegister,
    handleBlur,
    errors,
    touched,
    isLoading: isLoading || isRedirecting,
    passwordError,
    isRedirecting,
  };
};

export default useRegister;
